import { DataSourceOrigin, FieldTypes } from '../helpers/constants';
import { FieldProperties } from './Constants';
import { throwIfParameterMissing, getLocale } from '../helpers/utility';
import { getCurrentUser, getCurrentUserGroupIds } from '../helpers/UserUtility';
import { getDateTimeFormatInfo } from '../helpers/DateTimeUtility';
import { canIUse, Features } from '../helpers/PermissionUtility';
import { Template, TemplateField } from '../types/template-types';
import { CalculatorProcessPayload, CalculatorValue } from './calculation-types';

export default class CalculationRunner {
  private _formId: string;
  private _template: Template;
  private _getItemCollection: VoidFunction | TypeMePlease;
  private _isPublicForm: boolean;
  private _fieldList: TemplateField[];
  private _formulaList: TypeMePlease[];
  private _tableList: TypeMePlease[];
  private _calculationEngine: TypeMePlease;
  private _dialect: TypeMePlease;

  // public??
  Calculator: TypeMePlease;
  FormCalculatorContext: TypeMePlease;
  FunctionDialectBuilder: TypeMePlease;

  constructor(formId: string, template?: Template, getItemCollection?: (id: TypeMePlease) => TypeMePlease, isPublicForm = false) {
    if (formId === undefined) {
      throwIfParameterMissing('formId');
    }

    if (template === undefined) {
      throwIfParameterMissing('template');
    }

    this._formId = formId;
    this._template = template;
    this._getItemCollection = getItemCollection || (() => {});
    this._isPublicForm = isPublicForm;
    this._fieldList = template ? template.fields : [];
    this._formulaList = [];
    this._tableList = [];

    this.process = this.process.bind(this);
  }

  async init() {
    this.Calculator = await import(/* webpackChunkName: "calculator-js" */ 'calculator-js');
    this.FormCalculatorContext = (await import(/* webpackChunkName: "FormCalculatorContext" */ './FormCalculatorContext')).default;
    this.FunctionDialectBuilder = (await import(/* webpackChunkName: "FunctionDialectBuilder" */ './FunctionDialectBuilder')).default;

    this._calculationEngine = new this.Calculator.CalculationEngine();
    this._dialect = this.FunctionDialectBuilder.build(this._template?.namedFunctions);

    await this.Calculator.SetCulture(getLocale());

    const ctx = new this.FormCalculatorContext();
    this._buildNonValueFormulaList();
    this._buildFormulaListAndTableList();
    this._loadTablesIntoContext(ctx);
    this._calculationEngine.LoadFormulas(this._formulaList, ctx);
  }

  _buildFormulaListAndTableList() {
    for (const field of this._fieldList) {
      if (field.type === FieldTypes.Table) {
        // add to the table list
        this._addTable(field);

        // add to the formulaList
        for (const column of field.columns) {
          const defaultValues = column.defaultValues || [];
          for (let rowNumber = 0; rowNumber < field.rowCount; rowNumber++) {
            const formulaData = new this.Calculator.FormulaData();
            formulaData.Id = `${field.name} ${column.sortOrder + 1} ${rowNumber + 1}`;
            formulaData.Target = new this.Calculator.Field();
            formulaData.Target.Properties.setItem(FieldProperties.FieldType, column.type);
            formulaData.Target.Properties.setItem(FieldProperties.RequireMapImage, column.requireMapImage);
            formulaData.Target.Properties.setItem(FieldProperties.BackgroundCropDimensions, field.backgroundCropDimensions);
            formulaData.Target.Properties.setItem(FieldProperties.DateTimeFormat, column.dateTimeFormat);
            formulaData.Target.Id = field.id;
            formulaData.Target.Column = column.columnNumber;
            formulaData.Target.Row = rowNumber;
            formulaData.Target.ParentTableName = field.name;
            formulaData.Target.Name = `${field.name} ${column.sortOrder + 1} ${rowNumber + 1}`;
            formulaData.Target.DefaultValueOnError = column.type === FieldTypes.CheckBox ? 'false' : '';
            formulaData.Target.AllowOverride = column.allowOverride;
            formulaData.Target.Formula = defaultValues.length > rowNumber ? defaultValues[rowNumber] : null;
            formulaData.Formula =
              defaultValues.length > rowNumber ? this._convertRowColumnFunctions(defaultValues[rowNumber], column.sortOrder, rowNumber) : null;
            formulaData.Format = column.formatting;
            formulaData.ClearOnChange = column.clearOnFieldChange;
            this._formulaList.push(formulaData);
          }
        }
      } else {
        const formulaData = new this.Calculator.FormulaData();
        formulaData.Id = field.name;
        formulaData.Target = new this.Calculator.Field();
        formulaData.Target.Properties.setItem(FieldProperties.FieldType, field.type);
        formulaData.Target.Properties.setItem(FieldProperties.RequireMapImage, field.requireMapImage);
        formulaData.Target.Properties.setItem(FieldProperties.BackgroundCropDimensions, field.backgroundCropDimensions);
        formulaData.Target.Properties.setItem(FieldProperties.DateTimeFormat, field.dateTimeFormat);
        formulaData.Target.Id = field.id;
        formulaData.Target.Name = field.name;
        formulaData.Formula = field.defaultValue;
        formulaData.Target.DefaultValueOnError = field.type === FieldTypes.CheckBox ? 'false' : '';
        formulaData.Target.AllowOverride = field.allowOverride;
        formulaData.Target.Formula = field.defaultValue;
        formulaData.Format = field.formatting;
        formulaData.ClearOnChange = field.clearOnFieldChange;
        this._formulaList.push(formulaData);
      }
    }
  }

  _buildNonValueFormulaList() {
    if (!this._template) {
      return;
    }

    if (this._template.pages) {
      for (const page of [...this._template.pages].sort((a, b) => a.number - b.number)) {
        if (page.formulas) {
          for (const formula of page.formulas) {
            const formulaData = new this.Calculator.FormulaData();
            formulaData.Id = `${page.id}_${formula.key}`;
            formulaData.PropertyName = formula.key;
            formulaData.Target = new this.Calculator.Field();
            formulaData.Target.Name = page.id;
            formulaData.Target.Id = page.id;
            formulaData.Formula = formula.value;
            this._formulaList.push(formulaData);
          }
        }

        if (page.fieldPositions) {
          for (const fieldPosition of [...page.fieldPositions].sort((a, b) => a.y - b.y)) {
            const formulas = fieldPosition.formulas;
            if (formulas) {
              for (const formula of formulas) {
                const formulaData = new this.Calculator.FormulaData();
                formulaData.Id = `${fieldPosition.id}_${formula.key}`;
                formulaData.PropertyName = formula.key;
                formulaData.Target = new this.Calculator.Field();
                formulaData.Target.Name = fieldPosition.id;
                formulaData.Target.Id = fieldPosition.id;
                formulaData.Formula = formula.value;
                this._formulaList.push(formulaData);
              }
            }
          }
        }
      }
    }

    if (this._template.dataLayouts) {
      for (const dataLayout of this._template.dataLayouts) {
        for (const dataLayoutTab of dataLayout.dataLayoutTabs) {
          if (dataLayoutTab.formulas) {
            for (const formula of dataLayoutTab.formulas) {
              const formulaData = new this.Calculator.FormulaData();
              formulaData.Id = `${dataLayoutTab.id}_${formula.key}`;
              formulaData.PropertyName = formula.key;
              formulaData.Target = new this.Calculator.Field();
              formulaData.Target.Name = dataLayoutTab.id;
              formulaData.Target.Id = dataLayoutTab.id;
              formulaData.Formula = formula.value;
              this._formulaList.push(formulaData);
            }
          }

          if (dataLayoutTab.sections) {
            for (const section of dataLayoutTab.sections) {
              if (section.formulas) {
                for (const formula of section.formulas) {
                  const formulaData = new this.Calculator.FormulaData();
                  formulaData.Id = `${section.id}_${formula.key}`;
                  formulaData.PropertyName = formula.key;
                  formulaData.Target = new this.Calculator.Field();
                  formulaData.Target.Name = section.id;
                  formulaData.Target.Id = section.id;
                  formulaData.Formula = formula.value;
                  this._formulaList.push(formulaData);
                }
              }

              if (section.positions) {
                for (const position of section.positions) {
                  const formulas = position.formulas;
                  if (formulas) {
                    for (const formula of formulas) {
                      const formulaData = new this.Calculator.FormulaData();
                      formulaData.Id = `${position.id}_${formula.key}`;
                      formulaData.PropertyName = formula.key;
                      formulaData.Target = new this.Calculator.Field();
                      formulaData.Target.Name = position.id;
                      formulaData.Target.Id = position.id;
                      formulaData.Formula = formula.value;
                      this._formulaList.push(formulaData);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    if (this._fieldList) {
      for (const field of this._fieldList) {
        const fieldFormulas = field.formulas;
        const validationSettings = field.validationSettings;

        if (fieldFormulas) {
          for (const formula of fieldFormulas) {
            const formulaData = new this.Calculator.FormulaData();
            formulaData.Id = `${field.id}_${formula.key}`;
            formulaData.PropertyName = formula.key;
            formulaData.Target = new this.Calculator.Field();
            formulaData.Target.Id = field.id;
            formulaData.Target.Name = field.name;
            formulaData.Formula = formula.value;
            this._formulaList.push(formulaData);
          }
        }

        if (validationSettings) {
          for (const fieldValidationSettings of validationSettings) {
            const formula = fieldValidationSettings.formulas[0];
            const formulaData = new this.Calculator.FormulaData();
            formulaData.Id = `${field.id}_${formula.key}`;
            formulaData.PropertyName = formula.key;
            formulaData.Target = new this.Calculator.Field();
            formulaData.Target.Id = field.id;
            formulaData.Target.Name = field.name;
            formulaData.Target.Properties.setItem(FieldProperties.FieldValidationErrorSettings, fieldValidationSettings.errorSettings);
            formulaData.Formula = formula.value;
            this._formulaList.push(formulaData);
          }
        }
      }
    }

    const defaultFormName = this._template.defaultFormName;
    if (defaultFormName && defaultFormName.substring(0, 1) === '=') {
      const formulaData = new this.Calculator.FormulaData();
      formulaData.Id = 'TEMPLATE_DEFAULTFORMNAME';
      formulaData.Target = new this.Calculator.Field();
      formulaData.Target.IsFormName = true;
      formulaData.Target.Formula = defaultFormName;
      formulaData.Formula = defaultFormName;
      this._formulaList.push(formulaData);
    }

    const autoTags = this._template.autoTags;
    if (autoTags && autoTags.length > 0) {
      autoTags.forEach((autoTag) => {
        const formulaData = new this.Calculator.FormulaData();
        formulaData.Id = autoTag.id;
        formulaData.Target = new this.Calculator.Field();
        formulaData.Target.AutoTagId = autoTag.id;
        formulaData.Target.Formula = autoTag.formula;
        formulaData.Formula = autoTag.formula;
        this._formulaList.push(formulaData);
      });
    }
  }

  _addTable(field) {
    const table = new this.Calculator.Table();
    table.Id = field.id;
    table.Name = field.name;
    table.Columns = [];

    for (const column of field.columns) {
      const col = new this.Calculator.Column();
      col.Id = column.id;
      col.Name = column.name;
      col.SortOrder = column.sortOrder;
      col.ColumnNumber = column.columnNumber;

      table.Columns.push(col);
    }

    this._tableList.push(table);
  }

  _loadTablesIntoContext(context) {
    for (const table of this._tableList) {
      context.AddTable(table);
    }
  }

  _loadUserIntoContext(context) {
    const currentUser = getCurrentUser();
    context.AddEnvironmentVariable('CURRENTUSER_ID', currentUser.id);
    context.AddEnvironmentVariable('CURRENTUSER_FIRSTNAME', currentUser.firstName);
    context.AddEnvironmentVariable('CURRENTUSER_LASTNAME', currentUser.lastName);
    context.AddEnvironmentVariable('CURRENTUSER_PHONENUMBER', currentUser.phoneNumber);
    context.AddEnvironmentVariable('CURRENTUSER_USERNAME', currentUser.email);

    context.CurrentUserGroupIds = getCurrentUserGroupIds();
  }

  _loadTargetIntoContext(context, target, formValues, newValue: CalculatorValue, calcName) {
    context.AddEnvironmentVariable(this.FormCalculatorContext.EditedFieldKey, calcName);

    if (target) {
      context.AddEnvironmentVariable(this.FormCalculatorContext.EditedFieldNewValueKey, newValue?.value);

      if (target.type !== null && target.type !== undefined) {
        const oldValue = formValues.find((v) => v.fieldId === target.id);
        context.AddEnvironmentVariable(this.FormCalculatorContext.EditedFieldIdKey, target.id);
        context.AddEnvironmentVariable(this.FormCalculatorContext.EditedFieldOldValueKey, oldValue ? oldValue.value : null);
      }
    }
  }

  _clearSignatures(calcName, onFormulaResult) {
    const retval = [];
    const calculables = this._getCalculables();
    const clearableFields = this._getClearableFields();

    clearableFields.forEach((cf) => {
      if (cf !== calcName && calculables.has(cf)) {
        retval.push(new this.Calculator.Calculation(calculables.get(cf), '', ''));
      }
    });
    if (onFormulaResult) {
      for (const clearSignatureResult of retval) {
        onFormulaResult(clearSignatureResult);
      }
    }
    return retval;
  }

  _setDropDownDefaultValuesForField(ctx, field, name) {
    let items = null;
    if (field.items) {
      items = field.items;
    } else if (field.itemCollectionTemplateId) {
      const itemCollection = this._getItemCollection(field.itemCollectionTemplateId);
      if (itemCollection) {
        items = itemCollection.items;
      }
    }
    if (items) {
      items.forEach((item) => {
        if (item.isDefault) {
          ctx.SetPlainData(name, item.value);
        }
      });
    }
  }

  _setDropDownDefaultValues(ctx) {
    for (const field of this._fieldList) {
      if (field.type === FieldTypes.DropDown) {
        this._setDropDownDefaultValuesForField(ctx, field, field.name);
      } else if (field.type === FieldTypes.Table) {
        for (const column of field.columns) {
          if (column.type === FieldTypes.DropDown) {
            for (let rowNumber = 0; rowNumber < field.rowCount; rowNumber++) {
              this._setDropDownDefaultValuesForField(ctx, column, `${field.name} ${column.sortOrder + 1} ${rowNumber + 1}`);
            }
          }
        }
      }
    }
  }

  _createContext(dialect, target, formValues, newValue: CalculatorValue = null, calcName = '', dataSourceOrigin: DataSourceOrigin = DataSourceOrigin.Client) {
    const ctx = new this.FormCalculatorContext(dialect);
    this._loadTablesIntoContext(ctx);
    this._loadUserIntoContext(ctx);
    this._loadTargetIntoContext(ctx, target, formValues, newValue, calcName);

    ctx.AddFormValues(this._fieldList, formValues);

    ctx.LoadDateFormatterIntoContext(getDateTimeFormatInfo());
    ctx.AddEnvironmentVariable('FORM_ID', this._formId);
    ctx.AddEnvironmentVariable('PUBLICFORM', this._isPublicForm);

    const calculatorValue = new this.Calculator.CalculationValue();
    calculatorValue.Value = newValue?.value;
    calculatorValue.DateValue = newValue?.dateValue;
    ctx.SetData(calcName, calculatorValue);
    ctx.DataSourceOrigin = dataSourceOrigin;

    ctx.cachedDataRows = {};

    return ctx;
  }

  async getRemoteFilter(filter, indexes, formValues, currentTableName, dataSourceOrigin: DataSourceOrigin = DataSourceOrigin.Client) {
    const dialect = this.FunctionDialectBuilder.buildRemoteFilter();
    const ctx = this._createContext(dialect, null, formValues, null, '', dataSourceOrigin);
    if (indexes) {
      ctx.SetIndexes(indexes);
    }
    if (currentTableName) {
      ctx.LoadCurrentTableName(currentTableName);
    }

    const task = this._calculationEngine.ExecuteFormula(ctx, filter);
    await this.Calculator.Async.wait(task);

    return task.result;
  }

  async process({
    target = null,
    formValues = [],
    newValue = null,
    calcName = '',
    runOnLoadCalcsOnly = false,
    onFormulaResult = null
  }: CalculatorProcessPayload = {}) {
    if (!canIUse(Features.FieldCalculations)) {
      return { calculations: [] };
    }

    let calculationResult;

    if (!target || this._calculationEngine.CanRunFormula(calcName)) {
      const ctx = this._createContext(this._dialect, target, formValues, newValue, calcName);

      if (target) {
        const calculatorValue = new this.Calculator.CalculationValue();
        calculatorValue.Value = newValue?.value;
        calculatorValue.DateValue = newValue?.dateValue;
        ctx.SetData(calcName, calculatorValue);
      } else if (!runOnLoadCalcsOnly) {
        //Only set default values on form creation
        this._setDropDownDefaultValues(ctx);
      }

      const task = this._calculationEngine.RunFormulaList(ctx, calcName, runOnLoadCalcsOnly, onFormulaResult);

      try {
        await this.Calculator.Async.wait(task, 300000);

        calculationResult = this._convertRunFormulaListResult(task.result);

        if (target) {
          calculationResult.calculations = [...calculationResult.calculations, ...this._clearSignatures(calcName, onFormulaResult)];
        }
      } catch (err) {
        console.error(err);
        console.log(ctx);
        calculationResult = [];
        calculationResult.push(new this.Calculator.Calculation(target, newValue?.value, ''));
        calculationResult.calculations = [...this._clearSignatures(calcName, onFormulaResult)];
      }
    } else {
      calculationResult = [];
      calculationResult.push(new this.Calculator.Calculation(target, newValue?.value, ''));
      calculationResult.calculations = [...this._clearSignatures(calcName, onFormulaResult)];
    }

    return calculationResult;
  }

  async executeFormula(formula, formValues, target = null, newValue: CalculatorValue = null, calcName = '', currentTableName = null) {
    const ctx = this._createContext(this._dialect, target, formValues, newValue, calcName);
    if (currentTableName) {
      ctx.LoadCurrentTableName(currentTableName);
    }
    const task = this._calculationEngine.ExecuteFormula(ctx, formula);
    await this.Calculator.Async.wait(task);

    return task.result;
  }

  _convertRunFormulaListResult(runFormulaListResult) {
    const calculations = [];
    const formulaErrors = [];
    for (const item of runFormulaListResult.Calculations._items) {
      if (item) {
        calculations.push(item);
      }
    }

    for (const item of runFormulaListResult.FormulaErrors._items) {
      if (item) {
        formulaErrors.push(item);
      }
    }

    return { calculations, formulaErrors };
  }

  _getClearableFields() {
    const clearableFields = [];
    for (const f of this._calculationEngine.ClearableFields._items) {
      if (f) {
        clearableFields.push(f);
      }
    }

    return clearableFields;
  }

  _getCalculables() {
    const calculablesMap = new Map();
    const calculables = this._calculationEngine.GetCalculables();
    for (const propName in calculables.entries) {
      if (propName) {
        calculablesMap.set(calculables.entries[propName].key, calculables.entries[propName].value);
      }
    }

    return calculablesMap;
  }
  _convertRowColumnFunctions(formula, sortOrder, rowNumber) {
    if (!formula || formula[0] !== '=') {
      return formula;
    }

    const colPattern = 'Column\\(\\)';
    const rowPattern = 'Row\\(\\)';

    formula = formula.replace(new RegExp(colPattern, 'gi'), sortOrder + 1);
    formula = formula.replace(new RegExp(rowPattern, 'gi'), rowNumber + 1);

    return formula;
  }
}
